import React, {createRef, Fragment, useEffect, useRef, useState} from 'react';
import {useQueryClient} from "react-query";
import moment from "moment";
import axios from "axios";
import __ from "lodash";
import { v4 as uuidv4 } from 'uuid';
import {GetApp} from "@material-ui/icons";
import {Grid, IconButton, InputAdornment} from "@material-ui/core";
import {getApiSitePrefix} from "../../../functions/SiteDefaults";
import {formatChars} from "../../../blocks/FormatCharacters";
import {MaskedText, FormDialog, ComboSelect, DatePicker, CheckboxEditor, MaskedNumber} from "../../../controls";
import Validator from "../validators/AddContractDialogValidator";
import useTripDepartmentUsers from "../../../services/auth/useTripDepartmentUsers";
import ActionsBar from '../../../controls/ActionsBar';
import CompanyUuidLookup from "../../Companies/components/CompanyUuidLookup";
import ContractRoutesEditor from "./ContractRoutesEditor";
import ContractRouteSectorEditor from "./ContractRoutesSectorEditor";
import ContractRouteSurchargeEditor from "./ContractRoutesSurchargeEditor";
import {ContractCostingUuidLookup} from "./ContractCostingUuidLookup";
import CostingRevenueEditor from "../../Costings/components/CostingRevenueEditor";
import {fetch as fetchCosting} from "../../../services/costings/useCostings";
import {fetch as fetchCargo} from "../../../services/costings/useCostingCargo";
import {fetch as fetchSectors} from "../../../services/costings/useCostingSectors";
import {fetch as fetchServices} from "../../../services/costings/useCostingServices";
import {fetch as fetchCommissions} from "../../../services/costings/useCostingCommissions";
import BuildPrimaryRouteFromLegs from "../../Trips/utils/BuildPrimaryRouteFromLegs";
import ContractCargoEditor from "./ContractCargoEditor";
import NewCompanyCaptureDialog from "../../Companies/components/NewCompanyCaptureDialog";
import CurrencyLookup from '../../Currencies/components/CurrencyLookup';

const apiSitePrefix = getApiSitePrefix();
const emptyContract = {
  contractName: "",
  contractType: "",
  contractedPartyUuid: null,
  companyContactUuid: null,
  contractReference: "",
  contractRevision: "",
  active: true,
  contractStartDate: null,
  contractEndDate: null,
  contractUuid: null,
  associatedCostingUuid: null,
  fuelAnnexUuid: null,
  fuelAnnexRequired : false,
  fuelAnnex: {},
  routes: [],
  sectors: [],
  surCharges: [],
  currencyCode: "USD",
}

const uniqueContractName = async (contractName) => {
  try {
    const response = await axios.get(`${apiSitePrefix}/contracts/checkname/${contractName}`);
    if (response && response.data) {
      return response.data.status
    } else {
      return false;
    }
  } catch (err) {
    console.error(err);
    return false;
  }
}

const getAnnexes = async (fuelAnnexUuid) => {
  try {
    const response = await axios.get(`${apiSitePrefix}/contracts/getAnnexes/${fuelAnnexUuid}`);
    if (response && response.data) {
      return response.data.values
    } else {
      return {
        fuelAnnex: {},
        routes: [],
        sectors: [],
        surCharges: [],
      }
    }
  } catch (err) {
    console.error(err);
    return {
      fuelAnnex: {},
      routes: [],
      sectors: [],
      surCharges: [],
    }
  }
}

const canAddRoute = (record) => {
  if (!record.contractType)
    return false;
  if (!record.contractStartDate)
    return false;
  if (!record.contractEndDate)
    return false;

  return true;
}

const SectionHeading = (props) => {
  const { children, ...other } = props;
  return (
    <div style={{marginTop: 10, marginBottom: 2, textAlign: "left", fontSize: "14px", fontWeight: "bold", ...other}}>
      {children}
    </div>
  );
}

const AddContractDialog = (props) => {
  const {SystemData, apiSitePrefix, extraKeys, handleAdd, handleUpdate, classes, defaultValues, canEdit} = props;
  const requestRef = createRef(0);
  const eventBus = SystemData.eventBus;
  const ownerDepartment = defaultValues?.ownerDepartment||'COMMERCIAL';
  const companyRef = useRef();

  const [dialogStates, setDialogStates] = useState({
    showDialog: false,
    record: __.cloneDeep(emptyContract),
    title: "Add New Contract",
    selectedRouteUuid: null,
    selectedRouteName: "",
    editMode: false,
  });

  const formId = 'Add New Contract';
  const dateFormat = "DD/MM/YYYY";

  const queryClient = useQueryClient();
  const departmentUsers = useTripDepartmentUsers({queryClient, extraKeys: [ownerDepartment]});

  useEffect(()=> {
    const open = async (_record) => {
      const title = "Add New Contract";
      let record = {...{...emptyContract, contractUuid: uuidv4()}, ..._record, fuelAnnexRequired: _record && _record.fuelAnnexUuid};

      setDialogStates(
        {
          ...dialogStates,
          showDialog:true,
          title,
          record: __.cloneDeep(record),
          editMode: false
        }
      );
    }

    const close = () => {
      setDialogStates({...dialogStates, showDialog:false, record: __.cloneDeep(emptyContract), title: 'Add Costing'});
    }

    const rowSelected = (routeRecord) => {
      setDialogStates({...dialogStates, selectedRouteUuid: routeRecord.fuelAnnexSectorUuid, selectedRouteName: routeRecord.sectorName});
    }

    const clearRowSelected = (routeRecord) => {
      setDialogStates({...dialogStates, selectedRouteUuid: null, selectedRouteName: ""});
    }

    const prepareEdit = (_record) => {
      const title = "Edit Contract";
      let values = {}
      let record = _record;
      let routes = [];

      const asyncFunctions = [getAnnexes(_record.fuelAnnexUuid)];
      if (_record.associatedCostingUuid) {
        asyncFunctions.push(fetchCosting({queryClient, extraKeys: [_record.associatedCostingUuid]}));
        asyncFunctions.push(fetchCargo({queryClient, extraKeys: [_record.associatedCostingUuid]}));
        asyncFunctions.push(fetchSectors({queryClient, extraKeys: [_record.associatedCostingUuid]}));
        asyncFunctions.push(fetchServices({queryClient, extraKeys: [_record.associatedCostingUuid]}));
        asyncFunctions.push(fetchCommissions({queryClient, extraKeys: [_record.associatedCostingUuid]}));
      }

      SystemData.toggleLoadingProgress(true, "Loading contract data...");
      Promise.all(asyncFunctions).then(([annexValues, costing, cargo, legs, services, commissions]) => {
        SystemData.toggleLoadingProgress(false);

        values = annexValues;
        routes = values.routes;

        if (_record.associatedCostingUuid && asyncFunctions.length > 1) {
          _record['cargo'] = cargo;
          _record['legs'] = legs;
          _record['services'] = services;
          _record['commissions'] = commissions;
          if (__.has(_record, "contractData")) {
            if (__.has(_record.contractData, "costingData")) {
              _record['costingData'] = _record.contractData.costingData;
            } else {
              _record['costingData'] = costing;
              _record.contractData['costingData'] = costing;
            }
            if (__.has(_record.costingData, "totalRevenue") && _record.revenueAmount === 0) {
              _record['revenueAmount'] = _record.costingData.totalRevenue;
            } 
            if (!_record['currencyCode']) _record['currencyCode'] = 'USD';

            if (__.has(_record.contractData, "cargoData")) {
              _record['cargo'] = _record.contractData.cargoData;
            } else {
              _record['cargo'] = cargo;
              _record.contractData['cargoData'] = cargo;
            }
          } else {
            _record['contractData'] =  {costingData: costing, cargoData: cargo};
          }
        }

        record = {..._record, ...values, fuelAnnexRequired: _record.fuelAnnexUuid !== null};

        setDialogStates(
          {
            ...dialogStates,
            showDialog:true,
            title,
            record: {...__.cloneDeep(record), editMode: true},
            selectedRouteUuid: routes.length > 0 ? routes[0].fuelAnnexSectorUuid : null,
            selectedRouteName: routes.length > 0 ? routes[0].sectorName : "",
            editMode: true
          }
        );
      }).catch(error => {
        SystemData.toggleLoadingProgress(false);
        SystemData.alert("Error", `Failed to retrieve contract data: ${error}`);
      });
    }

    eventBus.bind("AddContractDialog.open", open);
    eventBus.bind("AddContractDialog.close", close);
    eventBus.bind("AddContractDialog.routeSelected", rowSelected);
    eventBus.bind("AddContractDialog.clearRouteSelected", clearRowSelected);
    eventBus.bind("AddContractDialog.edit", prepareEdit);

    return () => {
      eventBus.unbind("AddContractDialog.open", open);
      eventBus.unbind("AddContractDialog.close", close);
      eventBus.unbind("AddContractDialog.routeSelected", rowSelected);
      eventBus.unbind("AddContractDialog.clearRouteSelected", clearRowSelected);
      eventBus.unbind("AddContractDialog.edit", prepareEdit);
    }
  }, [dialogStates, eventBus, queryClient, SystemData]);

  const userOptions = departmentUsers.data?departmentUsers.data.map((userOption, i) => {
    return {code: userOption.user_id, value: `${userOption.firstName} ${userOption.lastName}`};
  }).sort((u1, u2) => {
    if (u1.value === u2.value) return 0;
    if (u1.value < u2.value) return -1;
    return 1;
  }):null;

  const handleGetNextContractRef = (record, changeRecord) => {
    const year = moment().format('YY');
    const month = moment().format('MM');
    const requestNo = ++requestRef.current;
    axios.get(
      `${apiSitePrefix}/contracts/next/${year}/${month}`
    ).then(contract => {
      if (requestNo === requestRef.current) {
        if (contract.data) {
          changeRecord({...record, contractReference: contract.data});
        }
      }
    });
  }

  const handleRouteChange = async (changes, props) => {
    const {record, recordChange} = props;
    const {routes, resolve, reject} = changes;
    if (record.routes.length > routes.length)
    {
      // Route Deleted
      const deletedRoutes = __.differenceBy(record.routes, routes,"fuelAnnexSectorUuid");
      if (deletedRoutes && Array.isArray(deletedRoutes)) {
        deletedRoutes.forEach(route => {
            __.remove(record.sectors, (value, index, array) => {
            return value.fuelAnnexSectorUuid === route.fuelAnnexSectorUuid;
          });
        });
      }
      recordChange({...record, routes});
      console.log("Route Deleted");
      resolve();
    } else if (record.routes.length < routes.length) {
      // Route Added
      recordChange({...record, routes});
      console.log("Route Added");
      resolve();
    } else {
      // Route updated
      recordChange({...record, routes});
      console.log("Route Updated");
      resolve();
    }
  }

  const handleRouteSectorChanged = async (changes, props) => {
    const {record, recordChange} = props;
    const {fuelAnnexSectorUuid, sectors, resolve, reject} = changes;
    __.remove(record.sectors, (value, index, array) => {
      return value.fuelAnnexSectorUuid === fuelAnnexSectorUuid;
    });

    if (record.sectors.length > sectors.length)
    {
      // Sector Deleted
      recordChange({...record, sectors: [...record.sectors, ...sectors]});
      console.log("Sector Deleted");
      resolve();
    } else if (record.sectors.length < sectors.length) {
      // Sector Added
      recordChange({...record, sectors: [...record.sectors, ...sectors]});
      console.log("Sector Added");
      resolve();
    } else {
      // Sector updated
      recordChange({...record, sectors: [...record.sectors, ...sectors]});
      console.log("Sector Updated");
      resolve();
    }
  }

  const handleRouteSurchargeChanged = async (changes, props) => {
    const {record, recordChange} = props;
    const {fuelAnnexSectorUuid, surCharges, resolve, reject} = changes;
    __.remove(record.surCharges, (value, index, array) => {
      return value.fuelAnnexSectorUuid === fuelAnnexSectorUuid;
    });

    if (record.surCharges.length > surCharges.length)
    {
      // Surcharge Deleted
      recordChange({...record, surCharges: [...record.surCharges, ...surCharges]});
      console.log("Surcharge Deleted");
      resolve();
    } else if (record.sectors.length < surCharges.length) {
      // Surcharge Added
      recordChange({...record, surCharges: [...record.surCharges, ...surCharges]});
      console.log("Surcharge Added");
      resolve();
    } else {
      // Surcharge updated
      recordChange({...record, surCharges: [...record.surCharges, ...surCharges]});
      console.log("Surcharge Updated");
      resolve();
    }
  }

  const updateAssociatedCostingData = (record, costingUuid, costingData, recordChange) => {
    Promise.all([
      fetchCargo({ queryClient, extraKeys: [costingUuid] }),
      fetchSectors({ queryClient, extraKeys: [costingUuid] }),
      fetchServices({ queryClient, extraKeys: [costingUuid] }),
      fetchCommissions({ queryClient, extraKeys: [costingUuid] })
    ]).then(([cargo, legs, services, commissions]) => {
      costingData['cargo'] = cargo;
      costingData['legs'] = legs;
      costingData['services'] = services;
      costingData['commissions'] = commissions;
      const data = {
        ...record,
        associatedCostingUuid: costingUuid,
        contractData: { costingData: costingData, cargoData: cargo },
        costingData,
        cargo,
        revenueAmount: costingData.totalRevenue,
        currencyCode: 'USD'
      };
      recordChange(data);
    });
  }

  if (dialogStates.showDialog && departmentUsers?.data) {
    return (
      <>
        <FormDialog
          formId={formId}
          title={dialogStates.title}
          message=""
          open={dialogStates.showDialog}
          record={dialogStates.record}
          dialogOptions={{maxWidth: false}}
          classes={classes}
          validator={Validator}
          validatorPosition="top"
          buttons = {{
            reload:{icon: "refresh", key: "Reload Costing", label: "Reload Costing", color: "secondary", disabled: (status)=>{return !status?.record?.associatedCostingUuid || status.error}},
            save:{icon: "check_circle", key: "Save", label: "Save", color: "primary", disabled: (status)=>{return (status.error || status?.buttonState.disabled)}},
            cancel:{icon: "cancel", key: "Cancel", label: "Cancel", color: "primary"}
          }}
          onChange={(state) => {
            if (state.buttonPressed === "reload") {
              SystemData.confirm(
                "Reload",
                `Are you sure you want to reload this Costing?`,
                [
                  {icon: "check_circle", value: true, label: "Reload", options: {color:"primary"}},
                  {icon: "cancel", value: false, label: "Cancel", options: {color:"secondary"}}
                ],
                (response) => {
                  if (response) {
                    updateAssociatedCostingData(state.record, state.record.associatedCostingUuid, state.record.costingData, state.recordChange);
                  }
                }        
              );
            } else if (state.buttonPressed === "save") {
              if (dialogStates.editMode) {
                const record = state.record;
                if (!record.fuelAnnexRequired) {
                  record.fuelAnnexUuid = null;
                }
                handleUpdate.mutate({
                  extraKeys,
                  record,
                  postSuccess: (savedCosting) => {
                    setDialogStates({...dialogStates, showDialog: false, record: null})
                    if(props.afterSave) props.afterSave({...record, ...savedCosting?.record?.data});
                    queryClient.invalidateQueries(['Contracts', ...extraKeys]);
                  },
                  postError: (data) => {
                    if (data.errorMessage) {
                      SystemData.alert("Error", `${data.errorMessage}`);
                    } else {
                      SystemData.alert("Error", `An Unexpected Error has occurred.`);
                    }
                  }
                });
              } else {
                const record = state.record;
                handleAdd.mutate({
                  extraKeys,
                  record,
                  postSuccess: (savedCosting) => {
                    setDialogStates({...dialogStates, showDialog: false, record: null})
                    if(props.afterSave) props.afterSave({...record, ...savedCosting?.record?.data});
                  },
                  postError: (data) => {
                    if (data.errorMessage) {
                      SystemData.alert("Error", `${data.errorMessage}`);
                    } else {
                      SystemData.alert("Error", `An Unexpected Error has occurred.`);
                    }
                  }
                });
              }
            } else {
              setDialogStates({...dialogStates, showDialog: false, record: null});
            }
          }}

        >
          <div style={{maxWidth: 1570}}>
            <Grid container checkChildren={true} spacing={3}>
              <Grid item xs={2} checkChildren={true} style={{marginTop: 1}}>
                {record =>
                  <ComboSelect
                    id="contractType"
                    label="Contract Type"
                    group="CONTRACT TYPES"
                    subGroup=""
                    fullWidth
                    value={props.value || ""}
                    inputLabelProps={{shrink: true}}
                    readOnly={!canEdit}
                    returnType={"objectOnly"}
                    onChange={(returnObject, props) => {
                      if (returnObject && !__.isEmpty(returnObject)) {
                        let companyType = "ANY";
                        let contractedPartyUuid = props.record.contractedPartyUuid;
                        if (__.has(returnObject, "data") && __.has(returnObject.data, "companyType")) {
                          companyType = returnObject.data.companyType;
                        }
                        if (props.record.companyType !== companyType)
                          contractedPartyUuid = null;
                        props.recordChange({...props.record, contractType: returnObject.code, companyType: companyType, contractedPartyUuid});
                      }
                    }}
                  />
                }
              </Grid>
              <Grid item xs={3} checkChildren={true}>
                {(record, changeRecord, changeButtonState)=>
                  <MaskedText
                    id="contractName"
                    label="Contract Description"
                    inputLabelProps={{shrink: true}}
                    mask={"x".repeat(50)}
                    maskChar={""}
                    alwaysShowMask={false}
                    formatChars={formatChars}
                    readOnly={!canEdit}
                  />
                }
              </Grid>
              <Grid item xs={2} checkChildren={true} style={{marginTop: 1}}>
                {(record, changeRecord, changeButtonState)=>
                  <MaskedText
                    id="contractReference"
                    label="Reference"
                    inputLabelProps={{shrink: true}}
                    mask={`${process.env.REACT_APP_TRIP_PREFIX}${moment().format('YYMM')}-___`}
                    maskChar={""}
                    alwaysShowMask={false}
                    formatChars={{'_':'[0-9]'}}
                    readOnly={!canEdit || record.createdAt}
                    inputProps={{
                      endAdornment:(
                        <InputAdornment position="end">
                          <IconButton
                            style={{padding:0}}
                            aria-label="Get Next Available Contract Reference"
                            disabled={!canEdit || record.createdAt}
                            onClick={()=>{
                              handleGetNextContractRef(record, changeRecord);
                            }}
                          >
                            <GetApp/>
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                    onChange={value => {
                      changeRecord({contractReference: value});
                    }}
                  />
                }
              </Grid>
              <Grid item xs={1} checkChildren={true} style={{marginTop: 1}}>
                {(record, changeRecord, changeButtonState)=>
                  <MaskedText
                    id="contractRevision"
                    label="Revision"
                    inputLabelProps={{shrink: true}}
                    mask={"x".repeat(50)}
                    maskChar={""}
                    alwaysShowMask={false}
                    formatChars={formatChars}
                    readOnly={!canEdit}
                    onChange={value => {
                      changeRecord({contractRevision: value});
                    }}
                  />
                }
              </Grid>
              <Grid item xs={2} checkChildren={true} style={{marginTop: 1}}>
                {(record, changeRecord) => (
                  <DatePicker
                    id="contractStartDate"
                    label="Start Date"
                    style={{width: "100%"}}
                    // minDate={record.takeOffTimeUTC}
                    // maxDate={maxDate}
                    // defaultValue={initialDate}
                    disableToolbar={true}
                    autoOk={true}
                    format={dateFormat}
                    readOnly={!canEdit}
                    onChange={(value, props) => {
                      const {record, recordChange} = props;
                      recordChange({
                        contractStartDate: moment.utc(value).format("YYYY-MM-DD")
                      });
                    }}
                  />
                )}
              </Grid>
              <Grid item xs={2} checkChildren={true} style={{marginTop: 1}}>
                {(record, changeRecord) => (
                  <DatePicker
                    id="contractEndDate"
                    label="End Date"
                    style={{width: "100%"}}
                    // minDate={record.takeOffTimeUTC}
                    // maxDate={maxDate}
                    // defaultValue={initialDate}
                    disableToolbar={true}
                    autoOk={true}
                    format={dateFormat}
                    readOnly={!canEdit}
                    onChange={(value, props) => {
                      const {record, recordChange} = props;
                      recordChange({
                        contractEndDate: moment.utc(value).format("YYYY-MM-DD")
                      });
                    }}
                  />
                )}
              </Grid>
              <Grid item xs={3} checkChildren={true} style={{marginTop: 1}}>
                {record =>
                  <CompanyUuidLookup
                    id="contractedPartyUuid"
                    label="Contracted Party"
                    shrink={true}
                    companyType={record.companyType ? record.companyType  : record.contractType === "CHARTER" ? "CLIENT" : (record.contractType === "SGHA" ? "SUPPLIER" : "ANY")}
                    value={record.contractedPartyUuid}
                    returnType={"object"}
                    includeNew={true}
                    readOnly={!canEdit || record.contractType === ""}
                    onChange={(returnObject, props) => {
                      if (returnObject.found) {
                        const contractedParty = returnObject.data;

                        if (contractedParty.companyCode === "NEW FORWARDER" && eventBus) {
                          eventBus.trigger('NewCompanyCapture.open');
                          document.getElementById("companyContactUuid").focus();
                          return;
                        }

                        const data = {
                          ...props.rowData,
                          "company.companyName": contractedParty.companyName,
                          contractedPartyUuid: contractedParty.companyUuid,
                        }
                        props.recordChange(data);
                      } else {
                        props.onChange(returnObject.value);
                      }
                    }}
                  />
                }
              </Grid>
              <Grid item xs={2} checkChildren={true} style={{marginTop: 1}}>
                {record =>
                  <MaskedText
                    id="companyContactUuid"
                    label="Company Contact"
                    readOnly={!canEdit || !record.contractedPartyUuid}
                    inputLabelProps={{shrink: true}}
                    fullWidth
                    mask={"x".repeat(50)}
                    maskChar={""}
                    alwaysShowMask={false}
                    formatChars={formatChars}
                  />
                }
              </Grid>
              <Grid item xs={3} checkChildren={true} style={{marginTop: 1}}>
                {(record, changeRecord) => (
                  <ContractCostingUuidLookup
                    label="Associated Costing"
                    id="associatedCostingUuid"
                    shrink={true}
                    fullWidth={true}
                    disableClearable={false}
                    allowEmpty={false}
                    value={record.associatedCostingUuid}
                    // companyUuid={record.contractedPartyUuid}
                    readOnly={!canEdit || record.contractType === "" ||  record.contractedPartyUuid === null}
                    returnType={"object"}
                    onChange={(returnObject, props) => {
                      if (returnObject.found) {
                        const costingData = returnObject.data
                        const costingUuid = returnObject.value;
                        if (costingUuid && costingUuid !== props.record.associatedCostingUuid) {
                          updateAssociatedCostingData(props.record, costingUuid, costingData, props.recordChange);
                        }
                      } else {
                        props.onChange(returnObject.value);
                      }                    
                    }}
                  />
                )}
              </Grid>
              <Grid item xs={1} checkChildren={true}>
                <CheckboxEditor
                  id="active"
                  label="Active"
                  readOnly={!canEdit}
                  onChange={(value, props) => {
                    if (value === false) {
                      props.recordChange({...props.record, active: false})
                    } else {
                      props.recordChange({...props.record, active: true})}
                  }}
                />
              </Grid>
              <Grid item xs={1} checkChildren={true}>
                <CheckboxEditor
                  id="fuelAnnexRequired"
                  label="Fuel Annex"
                  readOnly={!canEdit}
                  onChange={(value, props) => {
                    if (value === false) {
                      props.recordChange({...props.record, fuelAnnexRequired: false})
                    } else {
                      props.recordChange({...props.record, fuelAnnexRequired: true, fuelAnnexUuid: props.record.fuelAnnexUuid || uuidv4()})}
                  }}
                />
              </Grid>
              <Grid item xs={2} checkChildren={true}>
                <></>
              </Grid>
              {(record, changeRecord) => {
                if (!record.fuelAnnexRequired) {
                  return (
                    <Fragment checkChildren={true}>
                      <Grid item style={{flexBasis: "12.5%", maxWidth: "12.5%"}} checkChildren={true}>
                        <MaskedNumber
                          id="revenueAmount"
                          label={"Contracted Amount"}
                          decimalScale={2}
                          fixedDecimalScale={true}
                          thousandsGroupStyle=""
                          thousandSeparator=""
                          style={{fontSize: "0.875rem", ...props.style}}
                          fullWidth
                          inputLabelProps={{shrink: true}}
                        />            
                      </Grid>    
                      <Grid item xs={1} checkChildren={true}>
                        <CurrencyLookup
                          id="currencyCode"
                          label="Currency"
                          showChildCurrencies={false}
                          inputLabelProps={{shrink:true}}
                          style={{ fontSize: "0.875rem", textAlign: "right"}}
                        />
                      </Grid>    
                    </Fragment>
                  )
                }
              }}
              {(record, changeRecord) => {
                if (record.associatedCostingUuid && record.costingData)
                  return (
                    <Grid item xs={3} checkChildren={true}>
                      <div>
                        <div className="d-flex flex-row justify-content-between align-items-center">
                          <label htmlFor="primaryRoute" className="MuiFormLabel-root MuiInputLabel-root MuiInputLabel-animated MuiInputLabel-shrink">
                            Route
                          </label>
                        </div>
                        <div id="primaryRoute" style={{paddingTop: 6}}>
                          <BuildPrimaryRouteFromLegs legs={record.costingData?.legs}/>
                        </div>
                      </div>
                    </Grid>
                  );
              }}
              <Grid item xs={12} checkChildren={true}>
                {(record) => {
                  if (record.associatedCostingUuid && record.costingData)
                    return (
                      <div>
                        <SectionHeading>Cargo</SectionHeading>
                        <ContractCargoEditor
                          record={record}
                          extraKeys={extraKeys}
                          canEdit={true}
                          classes={classes}
                          onChange={(changedCargoDetails, props) => {
                            const {record, oldRecord, resolve, reject} = changedCargoDetails;
                            props.recordChange({...props.record, cargo: record.cargo, contractData: {...props.record.contractData, cargoData: record.cargo}})
                            resolve();
                          }}
                          defaultValues={defaultValues}
                          weightUom={record.weightUom}
                        />
                      </div>
                    );
                }}
              </Grid>
              <Grid item xs={12} checkChildren={true}>
                {(record, changeRecord, changeButtonState) => {
                  if (record.associatedCostingUuid && record.costingData)
                    return (
                      <div>
                        <SectionHeading>Revenue</SectionHeading>
                        <CostingRevenueEditor
                          record={record.costingData}
                          eventBus={eventBus}
                          systemData={SystemData}
                          canEdit={false}
                          readOnly={true}
                          noHeader={true}
                          classes={classes}
                          defaultValues={defaultValues}
                          rebuildRunning={(value) => {}}
                          changeButtonState={changeButtonState}
                        />
                      </div>
                    );
                }}
              </Grid>
            </Grid>
            {(record) => {
              if (record.fuelAnnexRequired)
                return (
                  <Grid container checkChildren={true} spacing={3}>
                    <Grid item xs={12} checkChildren={true}>
                      <div>
                        {(record, changeRecord, changeButtonState) => (
                          <ContractRoutesEditor
                            record={record}
                            // handleUpdate={handleAdd}
                            extraKeys={extraKeys}
                            canEdit={canEdit}
                            classes={classes}
                            onChange={handleRouteChange}
                            defaultValues={defaultValues}
                            weightUom={record.weightUom}
                            validFrom={record.contractStartDate}
                            validTo={record.contractEndDate}
                            changeButtonState={changeButtonState}
                          />
                        )}
                        {(record) => (
                          <ActionsBar
                            eventPrefix="ContractRoutesEditor"
                            record={record}
                            state={{}}
                            formDialog={{addRecordChange: true}}
                            actions={[
                              // {label: 'Calculate Route', key: 'Calculate Route', type: 'button', icon: "local_gas_station", color: "primary", disabled: (state) => {return !(record.aircraftReg && Array.isArray(state?.record?.cargo) && state.record.cargo.length > 0)}, eventName: 'AddCostingDialog.calculateRoute'},
                              {label: 'Add Route', key: 'Add Route', type: 'button', icon: "add_circle", color: "primary", disabled: (state) => {return !canEdit || !canAddRoute(record)}, eventName: 'ContractRoutesEditor.addRoute'}
                            ]}
                          />
                        )}
                      </div>
                    </Grid>
                    <Grid item xs={12} checkChildren={true}>
                      <div>
                        {(record, changeRecord, changeButtonState) => (
                          <ContractRouteSectorEditor
                            record={record}
                            handleUpdate={handleAdd}
                            extraKeys={extraKeys}
                            canEdit={canEdit}
                            classes={classes}
                            selectedRouteUuid={dialogStates.selectedRouteUuid}
                            selectedRouteName={dialogStates.selectedRouteName}
                            onChange={handleRouteSectorChanged}
                            changeButtonState={changeButtonState}
                          />
                        )}
                        {(record) => {
                          const actions = [];
                          actions.push({label: 'Add Route Sector', key: 'Add Route Sector', type: 'button', icon: "add_circle", color: "primary", disabled: (state) => {return (!canEdit || !dialogStates.selectedRouteUuid)}, eventName: 'ContractRouteSectorEditor.addSector'});
                          return (
                            <ActionsBar
                              eventPrefix="ContractRouteSectorEditor"
                              record={record}
                              state={{}}
                              formDialog={{addRecordChange: true}}
                              actions={actions}
                            />
                          )
                        }}
                      </div>
                    </Grid>
                    <Grid item xs={12} checkChildren={true}>
                      <div>
                        {(record, changeRecord, changeButtonState) => (
                          <ContractRouteSurchargeEditor
                            record={record}
                            handleUpdate={handleAdd}
                            extraKeys={extraKeys}
                            canEdit={canEdit}
                            classes={classes}
                            selectedRouteUuid={dialogStates.selectedRouteUuid}
                            selectedRouteName={dialogStates.selectedRouteName}
                            onChange={handleRouteSurchargeChanged}
                            changeButtonState={changeButtonState}
                          />
                        )}
                        {(record) => {
                          const actions = [];
                          actions.push({label: 'Add Route Surcharge', key: 'Add Route Surcharge', type: 'button', icon: "add_circle", color: "primary", disabled: (state) => {return (!canEdit || !dialogStates.selectedRouteUuid)}, eventName: "ContractRouteSurchargeEditor.addSurcharge"});
                          return (
                            <ActionsBar
                              eventPrefix="ContractRouteSurchargeEditor"
                              record={record}
                              state={{}}
                              formDialog={{addRecordChange: true}}
                              actions={actions}
                            />
                          )
                        }}
                      </div>
                    </Grid>
                  </Grid>
                );
            }}
            {(record, changeRecord) =>
              <NewCompanyCaptureDialog
                title={"Capture New Company Details"}
                open={false}
                onChange={(newCompany) => {
                  changeRecord({
                    "company.companyName": newCompany?.companyName,
                    contractedPartyUuid: newCompany?.companyUuid,
                  });
                  if (!newCompany?.companyUuid) {
                    setTimeout(() => {
                      document.getElementById("contractedPartyUuid").focus();
                    }, 100);
                  }
                }}
                checkChildren={true}
              />
            }
          </div>
        </FormDialog>
      </>
    )
  }
  return (<></>);
}

export default AddContractDialog;
